import { Directive, inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { CustomerFacade } from '../../facades/customer.facade';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import { LocalStorageUtils } from '../../utils/localStorage.utils';
import { EFeatureToggles, FEATURE_SETTING_WALK_ME_SCRIPT } from '../../configurations/common';
import { TranslateService } from '@ngx-translate/core';
import { ConfigurationFacade } from '../../facades/configuration.facade';

@Directive({
  selector: '[appWalkMe]',
  standalone: false,
})
export class WalkMeDirective implements OnInit, OnDestroy {

  script: any;
  WINDOW_LANGUAGE_PARAM: string = 'language';
  WINDOW_COUNTRY_PARAM: string = 'country';
  WINDOW_WALK_ME_LANGUAGE_PARAM: string = 'walkMeLanguage';
  private readonly unsubscribe$: Subject<void> = new Subject<void>();
  private callWalkMeSnipped: boolean = false;
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly customerFacade: CustomerFacade = inject(CustomerFacade);
  private readonly configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);
  private readonly renderer2: Renderer2 = inject(Renderer2);
  private readonly document: Document = inject(DOCUMENT);

  ngOnInit() {
    combineLatest([
      this.customerFacade.selectCustomerData(),
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.WALK_ME),
      this.translateService.get(FEATURE_SETTING_WALK_ME_SCRIPT),
    ]).pipe(takeUntil(this.unsubscribe$))
      .subscribe(([userData, walkMeEnabled, walkMeScript]) => {
        if (userData?.customerId && !this.callWalkMeSnipped && walkMeEnabled) {
          (window as any)['gid'] = userData.customerId;
          this.showWalkMe(walkMeScript);
          this.callWalkMeSnipped = true;
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.renderer2.removeChild(this.document.body, this.script);
    this.callWalkMeSnipped = false;
  }

  showWalkMe(walkMe: string): void {
    const localeSplit: string[] = LocalStorageUtils.getKeyValue('lang').split('-');
    const languageCode: string = localeSplit[0];
    const languageCountryCode: string = localeSplit[1];
    this.setLanguageAndCountry(window, languageCode, languageCountryCode);
    this.addWalkMe(walkMe);
  }

  addWalkMe(walkMeIdentifier: string): void {
    this.script = this.renderer2.createElement('script');
    this.script.type = `text/javascript`;
    this.script.text = this.getWalkMeScript(walkMeIdentifier);
    this.renderer2.appendChild(this.document.body, this.script);
  }

  getWalkMeScript(walkMeIdentifier: string): string {
    let WALK_ME_SCRIPT: string = '(function() {var walkme = document.createElement(\'script\'); ';
    WALK_ME_SCRIPT += this.getWalkMeLanguageConfigurationScript();
    WALK_ME_SCRIPT += 'walkme.type = \'text/javascript\'; walkme.async = true; ';
    WALK_ME_SCRIPT += 'walkme.src = \'' + walkMeIdentifier + '\'; ';
    WALK_ME_SCRIPT += 'var s = document.getElementsByTagName(\'script\')[0]; ';
    WALK_ME_SCRIPT += 's.parentNode.insertBefore(walkme, s); ';
    WALK_ME_SCRIPT += 'window._walkmeConfig = {smartLoad:true}; })();';
    return WALK_ME_SCRIPT;
  }

  /**
   * Language setting
   */
  getWalkMeLanguageConfigurationScript(): string {
    let WALK_ME_GET_LANGUAGE: string = 'window.walkme_get_language = function (){ ';
    WALK_ME_GET_LANGUAGE += 'if (window.walkMeLanguage) { return window.walkMeLanguage }';
    WALK_ME_GET_LANGUAGE += ' else {  return \'\' }';
    WALK_ME_GET_LANGUAGE += '}; ';
    return WALK_ME_GET_LANGUAGE;
  }

  /**
   * Configures WalkMe widget (window.country and window.language)
   * @param window
   * @param language
   * @param country
   */
  setLanguageAndCountry(window: Window, language: string, country: string): void {
    (window as any)[this.WINDOW_LANGUAGE_PARAM] = language;
    (window as any)[this.WINDOW_COUNTRY_PARAM] = country;
    if (language && country) {
      this.setWalkMeLanguage(language);
    }
  }

  private setWalkMeLanguage(walkMeLanguage: string): void {
    if (walkMeLanguage.toUpperCase() === 'EN') {
      delete (window as any)[this.WINDOW_WALK_ME_LANGUAGE_PARAM];
    } else {
      (window as any)[this.WINDOW_WALK_ME_LANGUAGE_PARAM] = walkMeLanguage;
    }
  }
}
